/**
 * S1 — AI use-case inventory: the banking_use_case_v1 landing surface
 * (DC-1781; accepted UX §5.1/§6; D-085). Rebuilt as the accepted analytical
 * workspace (D-BANK-UX-02): editorial header + derived stat strip, primary
 * action row, pressed-state filter chips, and a responsive card grid.
 *
 * Honesty rules carried in the markup, not the copy:
 *  - ARC scope renders "Not mapped" — mapping is DC-1782; nothing here
 *    fabricates a suggestion. A failed mapping-states read renders the stat
 *    as "—", never as zero mapped.
 *  - Status is a text+glyph chip, never color alone (A3).
 *  - The empty state offers both entry paths and the Library orientation
 *    link (§6.2); it is never a bare "No data".
 *  - A failed list is an error state with Retry; a failed imports read never
 *    blanks the grid (partial failure renders partially, §16.4).
 *  - Materiality tier is NOT computed in this slice; the card renders an
 *    honest "—" (SLOT-15: surfaced as unknown, never guessed).
 *  - Every filter chip is derived from values actually present in the loaded
 *    inventory — no chip offers a filter that could only return nothing.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import {
  ARC_SCOPE_STATES,
  CATEGORIES,
  LIFECYCLE_STAGES,
  RECORD_STATUS,
  downloadImportErrorReport,
  type DemoPackStatus,
  type ImportSummary,
  type UseCaseSummary,
} from './useCaseVocabulary';
import './banking-use-cases.css';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'loaded'; useCases: UseCaseSummary[] };

interface ListEnvelope {
  data?: { useCases?: UseCaseSummary[] };
}
interface ImportsEnvelope {
  data?: { imports?: ImportSummary[] };
}
interface StatesEnvelope {
  data?: { states?: Record<string, string> };
}
interface DemoPackEnvelope {
  data?: { demoPack?: DemoPackStatus };
}

const LIFECYCLE_LABELS: Record<string, string> = Object.fromEntries(
  LIFECYCLE_STAGES.map((s) => [s.value, s.label])
);

/** How the `?arc=` narrowing states itself in the grid. */
const ARC_FILTER_LABELS: Record<string, string> = {
  unmapped: 'not yet mapped',
  awaiting: 'awaiting validation',
  stale: 'out of date against the current catalog',
  validated: 'validated',
};

/** A filter chip row: one "All" reset plus one chip per value present in the data. */
function FilterChips(props: {
  legend: string;
  options: { value: string; label: string }[];
  selected: string;
  onSelect: (value: string) => void;
}): React.ReactElement | null {
  const { legend, options, selected, onSelect } = props;
  if (options.length === 0) return null;
  return (
    <div className="banking-filter-group" role="group" aria-label={legend}>
      <span className="banking-filter-legend">{legend}</span>
      <button
        type="button"
        className="banking-filter-chip"
        aria-pressed={selected === ''}
        onClick={() => onSelect('')}
      >
        All
      </button>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="banking-filter-chip"
          aria-pressed={selected === o.value}
          onClick={() => onSelect(selected === o.value ? '' : o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const UseCaseInventory: React.FC = () => {
  const { get, post, authFetch } = useApiClient();
  const { isAdmin } = usePermissions();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [latestImport, setLatestImport] = useState<ImportSummary | null>(null);
  const [arcStates, setArcStates] = useState<Record<string, string>>({});
  const [arcStatesKnown, setArcStatesKnown] = useState(false);
  const [demoPack, setDemoPack] = useState<DemoPackStatus | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const list = await get<ListEnvelope>('/api/use-cases');
      setState({ phase: 'loaded', useCases: list.data?.useCases ?? [] });
    } catch {
      setState({ phase: 'error' });
      return;
    }
    // Partial failure renders partially: an imports failure never blanks the grid.
    try {
      const imports = await get<ImportsEnvelope>('/api/use-cases/imports');
      setLatestImport(imports.data?.imports?.[0] ?? null);
    } catch {
      setLatestImport(null);
    }
    // ARC mapping states (DC-1782): a failure never blanks the grid; an
    // absent entry renders the honest "Not mapped", and an unread endpoint
    // leaves the mapped stat unknown rather than reporting zero.
    try {
      const states = await get<StatesEnvelope>('/api/use-cases/arc-mapping-states');
      setArcStates(states.data?.states ?? {});
      setArcStatesKnown(true);
    } catch {
      setArcStates({});
      setArcStatesKnown(false);
    }
    // Demo pack status (DC-1784): only consulted for the empty-state offer;
    // a failure simply means no offer is rendered.
    try {
      const demo = await get<DemoPackEnvelope>('/api/use-cases/demo-pack');
      setDemoPack(demo.data?.demoPack ?? null);
    } catch {
      setDemoPack(null);
    }
  }, [get]);

  const loadDemoPackNow = useCallback(async () => {
    setDemoBusy(true);
    setDemoError(null);
    try {
      await post('/api/use-cases/demo-pack/load', {});
      await load();
    } catch {
      setDemoError('The demo pack could not be loaded. The inventory was not changed.');
    } finally {
      setDemoBusy(false);
    }
  }, [post, load]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * `?arc=` narrows the grid to one ARC mapping position. The Overview's
   * next-decision actions land here, so the destination must be the actual
   * subset the decision named — not the whole grid with the reader left to
   * find it (D-BANK-UX-03). An unrecognised value narrows nothing.
   */
  const arcFilter = searchParams.get('arc') ?? '';
  const arcPredicate = useMemo((): ((state: string | undefined) => boolean) | null => {
    switch (arcFilter) {
      case 'unmapped':
        return (s) => !s || s === 'unmapped';
      case 'awaiting':
        return (s) => s === 'reviewer_unconfirmed' || s === 'partially_mapped';
      case 'stale':
        return (s) => s === 'stale_version';
      case 'validated':
        return (s) => s === 'validated';
      default:
        return null;
    }
  }, [arcFilter]);

  const filtered = useMemo(() => {
    if (state.phase !== 'loaded') return [];
    return state.useCases.filter(
      (uc) =>
        (!categoryFilter || uc.categoryPrimary === categoryFilter) &&
        (!lifecycleFilter || uc.lifecycleStage === lifecycleFilter) &&
        (!statusFilter || uc.recordStatus === statusFilter) &&
        // Only applied when the mapping states were actually read; filtering
        // on an unread state would silently present "no results" as a fact.
        (!arcPredicate || !arcStatesKnown || arcPredicate(arcStates[uc.id]))
    );
  }, [state, categoryFilter, lifecycleFilter, statusFilter, arcPredicate, arcStates, arcStatesKnown]);

  const groups = useMemo(() => {
    if (!groupByCategory) return [{ label: null as string | null, rows: filtered }];
    const byCategory = new Map<string, UseCaseSummary[]>();
    for (const uc of filtered) {
      const list = byCategory.get(uc.categoryPrimary) ?? [];
      list.push(uc);
      byCategory.set(uc.categoryPrimary, list);
    }
    return Array.from(byCategory.entries()).map(([label, rows]) => ({ label, rows }));
  }, [filtered, groupByCategory]);

  /** Chip options are the values the loaded inventory actually contains. */
  const chipOptions = useMemo(() => {
    const rows = state.phase === 'loaded' ? state.useCases : [];
    const present = (pick: (uc: UseCaseSummary) => string): Set<string> =>
      new Set(rows.map(pick));
    const categories = present((uc) => uc.categoryPrimary);
    const lifecycles = present((uc) => uc.lifecycleStage);
    const statuses = present((uc) => uc.recordStatus);
    return {
      categories: CATEGORIES.filter((c) => categories.has(c)).map((c) => ({
        value: c,
        label: c,
      })),
      lifecycles: LIFECYCLE_STAGES.filter((s) => lifecycles.has(s.value)).map((s) => ({
        value: s.value,
        label: s.label,
      })),
      statuses: Object.keys(RECORD_STATUS)
        .filter((s) => statuses.has(s))
        .map((s) => ({ value: s, label: RECORD_STATUS[s].label })),
    };
  }, [state]);

  const total = state.phase === 'loaded' ? state.useCases.length : null;
  const productionCount =
    state.phase === 'loaded'
      ? state.useCases.filter((uc) => uc.lifecycleStage === 'production').length
      : null;
  const mappedCount =
    state.phase === 'loaded' && arcStatesKnown
      ? state.useCases.filter((uc) => {
          const arc = arcStates[uc.id];
          return Boolean(arc) && arc !== 'unmapped';
        }).length
      : null;

  const stat = (value: number | null): string => (value === null ? '—' : String(value));

  return (
    <div className="banking-page use-case-inventory">
      <header className="banking-page-header banking-header-editorial">
        <div className="banking-header-lede">
          <p className="banking-eyebrow">AI USE CASE INVENTORY</p>
          <h1 className="banking-title banking-title-editorial">AI Use Cases</h1>
          <p className="banking-subtitle">
            The bank&apos;s own record of where AI operates — what each one does, who
            owns it, and how it is governed.
          </p>
        </div>
        <dl className="banking-stat-strip">
          {/* dt-before-dd keeps the list valid; the strip reverses it visually. */}
          <div className="banking-stat">
            <dt className="banking-stat-label">Registered</dt>
            <dd className="banking-stat-value">{stat(total)}</dd>
          </div>
          <div className="banking-stat">
            <dt className="banking-stat-label">In production</dt>
            <dd className="banking-stat-value">{stat(productionCount)}</dd>
          </div>
          <div className="banking-stat">
            <dt className="banking-stat-label">ARC scope mapped</dt>
            <dd className="banking-stat-value">{stat(mappedCount)}</dd>
          </div>
        </dl>
      </header>

      {latestImport && latestImport.rowCountRejected > 0 && (
        <div className="banking-import-banner" data-testid="import-banner" role="status">
          Import of <strong>{latestImport.sourceFilename}</strong> (template v
          {latestImport.templateVersion}): {latestImport.rowCountImported} imported,{' '}
          {latestImport.rowCountRejected} rejected.{' '}
          <button
            type="button"
            className="banking-link-button"
            data-testid="import-banner-report"
            onClick={() => void downloadImportErrorReport(authFetch, latestImport.id)}
          >
            Download error report (CSV)
          </button>{' '}
          <Link to="/use-cases/import">Review rejected rows →</Link>
        </div>
      )}

      <div className="banking-action-row">
        <Link to="/use-cases/new" className="banking-btn banking-btn-primary">
          Add use case
        </Link>
        <Link to="/use-cases/import" className="banking-btn banking-btn-secondary">
          Import template
        </Link>
      </div>

      {arcPredicate && (
        <div className="banking-scope-filter-note" data-testid="arc-filter-note">
          <span>
            Showing only use cases whose ARC scope is{' '}
            <b>{ARC_FILTER_LABELS[arcFilter]}</b>.
          </span>
          <button
            type="button"
            className="banking-link-button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('arc');
              setSearchParams(next, { replace: true });
            }}
          >
            Show all use cases
          </button>
        </div>
      )}

      <div className="banking-filters" role="group" aria-label="Inventory filters">
        <FilterChips
          legend="Category"
          options={chipOptions.categories}
          selected={categoryFilter}
          onSelect={setCategoryFilter}
        />
        <FilterChips
          legend="Lifecycle"
          options={chipOptions.lifecycles}
          selected={lifecycleFilter}
          onSelect={setLifecycleFilter}
        />
        <FilterChips
          legend="Status"
          options={chipOptions.statuses}
          selected={statusFilter}
          onSelect={setStatusFilter}
        />
        <div className="banking-filter-group">
          <button
            type="button"
            className="banking-filter-chip banking-filter-toggle"
            aria-pressed={groupByCategory}
            onClick={() => setGroupByCategory((v) => !v)}
          >
            Group by category
          </button>
        </div>
      </div>

      {state.phase === 'loading' && (
        <div
          className="banking-grid-skeleton"
          data-testid="use-case-grid-skeleton"
          aria-busy="true"
          aria-label="Loading use cases"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="banking-skeleton-row banking-skeleton-card" />
          ))}
        </div>
      )}

      {state.phase === 'error' && (
        <div className="banking-grid-error" data-testid="use-case-grid-error" role="alert">
          <p>
            The inventory could not be loaded. Your filters are preserved — retrying will
            not lose your view.
          </p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {state.phase === 'loaded' && state.useCases.length === 0 && (
        <div className="banking-grid-empty" data-testid="use-case-grid-empty">
          <h2>Register your first AI use case</h2>
          <p>
            A use case is the bank&apos;s own record of one place AI operates — what it
            does, who owns it, and how it is governed. The inventory is the entry point
            for everything Compass Banking assesses.
          </p>
          <div className="banking-empty-actions">
            <Link to="/use-cases/new" className="banking-btn banking-btn-primary">
              Add use case
            </Link>
            <Link to="/use-cases/import" className="banking-btn banking-btn-secondary">
              Import from template
            </Link>
          </div>
          <Link to="/arc-library" className="banking-empty-library-link">
            See what ARC covers →
          </Link>
          {isAdmin && demoPack && !demoPack.loaded && (
            <div className="banking-demo-pack-card" data-testid="demo-pack-card">
              <p className="banking-eyebrow">DEMO PACK · SYNTHETIC</p>
              <h3>Credit Scoring demo pack</h3>
              <p>
                Loads {demoPack.caseCount} fully synthetic AI use cases for{' '}
                {demoPack.institution} through the standard template import —
                the Credit Scoring function plus adjacent context, with
                provisional ARC identifier suggestions ready for review.
                Nothing is validated and nothing runs.
              </p>
              {demoError && (
                <div className="banking-create-error" data-testid="demo-pack-error" role="alert">
                  {demoError}
                </div>
              )}
              <button
                type="button"
                className="banking-btn banking-btn-secondary"
                data-testid="demo-pack-load"
                disabled={demoBusy}
                onClick={() => void loadDemoPackNow()}
              >
                {demoBusy
                  ? 'Loading demo pack…'
                  : `Load ${demoPack.caseCount} synthetic use cases`}
              </button>
            </div>
          )}
        </div>
      )}

      {state.phase === 'loaded' && state.useCases.length > 0 && (
        <div className="banking-grid-wrap" data-testid="use-case-grid">
          {groups.map((group) => (
            <section key={group.label ?? 'all'} className="banking-grid-section">
              {group.label && (
                <h2 className="banking-group-heading">
                  {group.label}{' '}
                  <span className="banking-group-count">{group.rows.length} use cases</span>
                </h2>
              )}
              <ul className="banking-card-grid">
                {group.rows.map((uc) => {
                  const status = RECORD_STATUS[uc.recordStatus] ?? {
                    label: uc.recordStatus,
                    glyph: '○',
                  };
                  const arcKey = arcStates[uc.id];
                  const arc = ARC_SCOPE_STATES[arcKey] ?? ARC_SCOPE_STATES.unmapped;
                  return (
                    <li key={uc.id} className="banking-uc-card">
                      <div className="banking-uc-card-head">
                        <span className="scope-code banking-uc-code">{uc.useCaseRef}</span>
                        <span className="banking-uc-lifecycle">
                          {LIFECYCLE_LABELS[uc.lifecycleStage] ?? uc.lifecycleStage}
                        </span>
                      </div>
                      <h3 className="banking-uc-name">{uc.name}</h3>
                      <p className="banking-uc-family">{uc.categoryPrimary}</p>
                      <p className="banking-uc-people">
                        <span className="banking-uc-people-label">Owner</span>{' '}
                        <span>{uc.accountableOwner}</span>
                      </p>
                      <div className="banking-uc-states">
                        {arc.label === 'Not mapped' ? (
                          <span className="banking-not-mapped">Not mapped</span>
                        ) : (
                          <span className={`banking-status-chip arc-${arcKey}`}>
                            <span aria-hidden="true">{arc.glyph}</span> {arc.label}
                          </span>
                        )}
                        <span className={`banking-status-chip status-${uc.recordStatus}`}>
                          <span aria-hidden="true">{status.glyph}</span> {status.label}
                        </span>
                        <span
                          className="banking-uc-materiality"
                          title="Materiality tier arrives with scope review — not computed yet, never guessed."
                        >
                          Materiality —
                        </span>
                      </div>
                      <div className="banking-uc-card-foot">
                        <Link to={`/use-cases/${uc.id}`} className="banking-uc-card-link">
                          Review scope →
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default UseCaseInventory;
